import { useEffect, useRef, useState } from 'react';

// Small tolerance to avoid jitter from sub-pixel layout differences.
const OVERFLOW_EPSILON = 1;

const trimTrailingWhitespace = (value: string) => value.replace(/\s+$/u, '');

const getLineHeight = (computedStyles: CSSStyleDeclaration) => {
  const parsedLineHeight = Number.parseFloat(computedStyles.lineHeight);
  const parsedFontSize = Number.parseFloat(computedStyles.fontSize);

  if (Number.isFinite(parsedLineHeight) && parsedLineHeight > 0) {
    return parsedLineHeight;
  }

  return parsedFontSize * 1.4;
};

const createMeasureElement = (contentElement: HTMLParagraphElement, contentWidth: number) => {
  // Clone the rendered paragraph so measurement inherits the same typography/wrapping rules.
  const measureElement = contentElement.cloneNode(false) as HTMLParagraphElement;
  measureElement.style.position = 'fixed';
  measureElement.style.left = '-9999px';
  measureElement.style.top = '0';
  measureElement.style.visibility = 'hidden';
  measureElement.style.pointerEvents = 'none';
  measureElement.style.margin = '0';
  measureElement.style.padding = '0';
  measureElement.style.width = `${contentWidth}px`;
  measureElement.style.maxHeight = 'none';
  measureElement.style.height = 'auto';
  measureElement.style.overflow = 'visible';

  document.body.appendChild(measureElement);

  return measureElement;
};

type UseCommentPreviewParams = {
  body: string;
  forceExpanded?: boolean;
  maxLines: number;
  previewSuffix?: string;
};

export const useCommentPreview = ({
  body,
  forceExpanded = false,
  maxLines,
  previewSuffix = '...',
}: UseCommentPreviewParams) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewBody, setPreviewBody] = useState(body);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    setIsExpanded(forceExpanded);
  }, [forceExpanded, body]);

  useEffect(() => {
    const containerElement = containerRef.current;
    const contentElement = contentRef.current;

    if (!containerElement || !contentElement) {
      return;
    }

    const recomputePreview = () => {
      const computedStyles = window.getComputedStyle(contentElement);
      const lineHeight = getLineHeight(computedStyles);
      const contentWidth = contentElement.clientWidth;

      if (!Number.isFinite(lineHeight) || lineHeight <= 0 || contentWidth <= 0) {
        setIsOverflowing(false);
        setPreviewBody(body);
        setIsExpanded(forceExpanded);
        return;
      }

      const maxHeight = lineHeight * maxLines;
      const measureElement = createMeasureElement(contentElement, contentWidth);

      try {
        measureElement.textContent = body;
        const fullHeight = measureElement.scrollHeight;
        const nextOverflowing = fullHeight - maxHeight > OVERFLOW_EPSILON;

        setIsOverflowing(nextOverflowing);

        if (!nextOverflowing) {
          setPreviewBody(body);
          setIsExpanded(forceExpanded);
          return;
        }

        let low = 0;
        let high = body.length;
        let bestPreview = previewSuffix;

        // Find the longest prefix that still fits in maxLines once suffix is appended.
        while (low <= high) {
          const middle = Math.floor((low + high) / 2);
          const prefix = trimTrailingWhitespace(body.slice(0, middle));
          const candidate = `${prefix}${previewSuffix}`;

          measureElement.textContent = candidate;

          if (measureElement.scrollHeight - maxHeight <= OVERFLOW_EPSILON) {
            bestPreview = candidate;
            low = middle + 1;
          } else {
            high = middle - 1;
          }
        }

        setPreviewBody(bestPreview);
      } finally {
        measureElement.remove();
      }
    };

    recomputePreview();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(recomputePreview);
    resizeObserver.observe(containerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [body, forceExpanded, maxLines, previewSuffix]);

  const shouldClamp = isOverflowing && !isExpanded && !forceExpanded;
  const displayedBody = shouldClamp ? previewBody : body;

  const expand = () => setIsExpanded(true);

  return {
    containerRef,
    contentRef,
    displayedBody,
    shouldClamp,
    expand,
  };
};
